<template>
  <page :name="name" :title="title" :state="[ticketState, messagesState, productState]" sticky>
    <template #breadcrumbs>
      <pf-breadcrumb-item :to="{ name: 'tickets' }">{{ $t('Tickets') }}</pf-breadcrumb-item>
    </template>

    <template #toolbar-post v-if="ticket.id || idTicket === 'new'">
      <template v-if="ticket.id">
        <pf-label v-if="ticket.urgente" color="red" class="pf-v6-u-mr-sm">
          <template #icon>
            <triangle-exclamation-icon />
          </template>
          {{ $t('Urgente') }}
        </pf-label>
        <pf-label v-if="ticket.prodotto" color="grey">
          {{ $t('Prodotto: {0}', [ticket.prodotto]) }}
        </pf-label>
        <pf-label v-if="ticket.department" color="grey" class="pf-v6-u-mr-sm">
          {{ $t('Tipologia: {0}', [ticket.department]) }}
        </pf-label>
        <pf-label :color="ticket.stato == 'aperto' ? 'green' : 'grey'">
          {{ $t('Stato: {0}', [ticket.stato == 'aperto' ? $t('Aperto') : $t('Chiuso')]) }}
        </pf-label>
      </template>
      <pf-label v-else color="grey">
          {{ $t('Nuovo') }}
      </pf-label>
    </template>

    <pf-page-section v-if="!ticket.id && idTicket !== 'new'">
      <pf-alert :title="$t('Ticket non trovato')" variant="danger">
        <pf-button variant="link" @click="idTicket = 'new'">{{ $t('Apri un nuovo ticket') }}</pf-button>
      </pf-alert>
    </pf-page-section>

    <template v-else>
      <pf-page-section v-if="ticket.id">
        <pf-empty-state v-if="!messages || !messages.length">
          <pf-empty-state-header :title="$t('Nessun messaggio presente')" heading-level="h4">
            <template #icon>
              <magnifying-glass-icon />
            </template>
          </pf-empty-state-header>
        </pf-empty-state>

        <pf-progress-stepper v-else vertical>
          <template v-for="message of messages" :key="message.id">
            <pf-progress-step>
              <template #icon>
                <img :src="avatar(message)" />
              </template>

              <pf-panel :class="['pf-m-ticket-message', { 'pf-m-ticket-operator-message': message.operatore }]">
                <pf-panel-header>
                  <pf-flex>
                    <pf-flex-item component="strong" v-if="message.operatore">{{ message.operatore }}</pf-flex-item>
                    <pf-flex-item component="strong" v-else-if="message.utente">{{ message.utente }}</pf-flex-item>
                    <pf-flex-item component="strong" v-else>{{ $t('Hai scritto:') }}</pf-flex-item>
                    <pf-flex-item>
                      <pf-content component="small">{{ $datetime(message.data) }}</pf-content>
                    </pf-flex-item>
                  </pf-flex>
                </pf-panel-header>
                <pf-panel-main>
                  <pf-panel-main-body>
                    <iframe-autoheight
                      v-if="message.operatore"
                      referrerpolicy="no-referrer"
                      sandbox="allow-same-origin allow-popups"
                      :srcdoc="messageHtml(message.messaggio)"
                    />
                    <template v-else>{{ message.messaggio }}</template>
                  </pf-panel-main-body>
                </pf-panel-main>
              </pf-panel>

              <div
                v-if="message.allegati.length > 0 || message.id_offerta_allegata || message.id_fattura_allegata"
                class="pf-v6-u-mt-sm"
              >
                {{ $t('Allegati') }}{{ ': ' }}
                <template v-if="message.allegati.length > 0">
                  <pf-label v-for="file in message.allegati" :key="file.id" :href="`/rest/ticket_attachment/${file.id}`">
                    <template #icon>
                      <paperclip-icon />
                    </template>
                    {{ file.filename }}
                  </pf-label>
                </template>
                <pf-label
                  v-if="message.id_offerta_allegata"
                  :key="message.id_offerta_allegata"
                  :href="`/rest/offer-pdf/${message.id_offerta_allegata}`"
                  color="orange"
                >
                  <template #icon>
                    <file-invoice-dollar-icon />
                  </template>
                  {{ $t('Offerta nr {0}', [message.id_offerta_allegata]) }}
                </pf-label>
                <pf-label
                  v-if="message.id_fattura_allegata"
                  :key="message.id_fattura_allegata"
                  :href="`/rest/invoice-pdf/${message.id_fattura_allegata}`"
                  color="green"
                >
                  <template #icon>
                    <file-lines-icon />
                  </template>
                  {{ $t('Fattura {0}', [`${message.nr_fattura}${message.serie_fattura} / ${message.anno_fattura}`]) }}
                </pf-label>
              </div>
            </pf-progress-step>
          </template>
        </pf-progress-stepper>
      </pf-page-section>

      <pf-divider v-if="ticket.id" />

      <pf-page-section v-if="ticket.stato == 'aperto' || !ticket.id">
        <pf-panel variant="bordered">
          <pf-panel-main>
            <pf-panel-main-body>
              <x-form
                ref="form"
                encoding="json"
                :action="'/rest/tickets' + (ticket.id ? `/${ticket.id}/messages` : '')"
                :additional-data="additionalData"
                :submit-text="confirmMessage"
                xhr
                @submit="send"
                style="max-width: 600px;"
              >
                <template v-if="!ticket.id">
                  <pf-flex>
                    <pf-flex-item>
                      <form-group :name="$t('Tipologia')" required>
                        <pf-form-select name="department" v-model="department">
                          <pf-form-select-option value="tecnica">
                            {{ $t('Tecnica') }}
                          </pf-form-select-option>
                          <pf-form-select-option value="amministrativa">
                            {{ $t('Amministrativa') }}
                          </pf-form-select-option>
                          <pf-form-select-option value="commerciale">
                            {{ $t('Commerciale') }}
                          </pf-form-select-option>
                        </pf-form-select>
                      </form-group>
                    </pf-flex-item>

                    <pf-flex-item v-if="products.length" style="max-width: 300px;">
                      <form-group :name="$t('Prodotto')" :required="department === 'tecnica'">
                        <template #field-help>
                          {{ $t('Seleziona il prodotto per cui vuoi richiedere assistenza') }}
                        </template>
                        <pf-form-select name="id_product" :required="department === 'tecnica'">
                          <pf-form-select-option v-for="product of products" :key="product.id" :value="product.id">
                            {{ product.dominio }} - {{ product.nome }}
                          </pf-form-select-option>
                        </pf-form-select>
                      </form-group>
                    </pf-flex-item>
                  </pf-flex>

                  <form-group :name="$t('Titolo')" required style="max-width: 600px;">
                    <x-input id="ticketTitle" name="titolo" type="text" :placeholder="$t('Titolo')" required />
                  </form-group>
                </template>

                <form-group :name="$t('Messaggio')" required>
                  <template #helper-text v-if="!ticket.id">
                    {{ $t('Specificare eventuale dominio di riferimento, IP del server dedicato o indirizzo del server cloud ed in generale un riferimento al servizio sul quale si richiede assistenza. Se possibile indicare come è possibile riprodurre il problema.',) }}
                  </template>
                  <pf-textarea
                    id="messageText"
                    name="messaggio"
                    :placeholder="$t('Testo del messaggio')"
                    rows="10"
                    required
                  />
                </form-group>

                <template v-if="areWeClosed">
                  <pf-alert
                    inline
                    v-if="!ticket.id"
                    variant="warning"
                    :title="$t('Al ticket verrà data risposta in orario di ufficio. In caso di urgenze selezionare la checkbox \'Urgente\'')"
                  />
                  <pf-alert
                    inline
                    v-else-if="!ticket.urgente"
                    variant="warning"
                    :title="$t('Al messaggio verrà data risposta in orario di ufficio. In caso di urgenze aprire un nuovo ticket di tipo \'Urgente\'')"
                  >
                    <router-link :to="{ name: 'ticket', params: { id: 'new' } }">{{ $t('Apri un nuovo ticket') }}</router-link>
                  </pf-alert>
                </template>

                <form-group v-if="!ticket.id" :name="$t('Urgente')" no-label>
                  <pf-checkbox name="urgente" value="1">
                    <template #label>{{ $t('Urgente') }}</template>

                    <template #description v-if="login.is_premium">
                      ({{ $t('Incluso in Prime') }})
                    </template>
                    <template #description v-else>
                      {{ $t('Il ticket verrà fatturato al costo di {0}', [formatCurrency(urgentTicketCost, login.currency)]) }}
                    </template>
                  </pf-checkbox>
                </form-group>

                <div v-if="attachments.length">
                  <label>{{ $t('Allegati') }}: </label>
                  <pf-label v-for="(file, index) in attachments" :key="index" @close="attachments.splice(index, 1)">
                    <template #icon>
                      <paperclip-icon />
                    </template>
                    {{ file.name }} ({{ $size(file.size) }})
                  </pf-label>
                </div>

                <template #submit>
                  <paper-plane-icon /> {{ $t('Invia') }}
                </template>

                <template #actions>
                  <pf-button component="label" variant="control" style="cursor: pointer">
                    <template #icon>
                      <paperclip-icon />
                    </template>
                    {{ $t('Aggiungi allegati') }}
                    <input
                      type="file"
                      multiple
                      style="display: none"
                      @change="refreshAttachments"
                    >
                  </pf-button>
                </template>
              </x-form>
            </pf-panel-main-body>
          </pf-panel-main>
        </pf-panel>
      </pf-page-section>
    </template>
  </page>
</template>

<style lang="scss" scoped>
.pf-v6-c-progress-stepper {
  --pf-v6-c-progress-stepper__step-main--MarginTop: 0;
  --pf-v6-c-progress-stepper__step-icon--Width: 3rem;
  --pf-v6-c-progress-stepper__step-icon--Height: 3rem;
  --pf-v6-c-progress-stepper__step-icon--ZIndex: 0;
}

.pf-v6-c-progress-stepper img {
  border-radius: 50%;
}

.pf-v6-c-panel.pf-m-ticket-message {
  iframe {
    width: 100%;
  }

  .pf-v6-c-panel__header {
    padding-top: 0;
    padding-bottom: 0;
    line-height: var(--pf-v6-c-progress-stepper__step-icon--Height);
  }

  .pf-v6-c-panel__main {
    white-space: pre-wrap;
    max-width: 80ch;
    overflow: auto;
    border-radius: 10px;
    background-color: var(--pf-v6-c-page--BackgroundColor);
  }

  &.pf-m-ticket-operator-message {
    white-space: initial;

    .pf-v6-c-panel__header strong {
      color: var(--pf-t--global--text--color--brand--default);
    }
  }
}
</style>

<script lang="ts" setup>
import md5 from 'nano-md5';

import IframeAutoheight from '@common/IframeAutoheight.vue';
import PaperclipIcon from '@vue-patternfly/icons/paperclip-icon';
import PaperPlaneIcon from '@vue-patternfly/icons/paper-plane-icon';

import { asyncComputed, asyncComputedList } from '@common/asyncComputed';
import { formatCurrency } from '@common/utils';
import { useAppStore } from '@/store/app';
import { useLoginStore } from '@/store/login';
import { type PHPDateTime, type Ticket, UrgentTicketCostResource, AreWeClosedResource, type TicketMessage, TicketResource, ProductOrdersResource } from '@/resources';
import { ref, type Ref, computed } from 'vue';
import { $t as _t } from '@/i18n';
import { pop } from '@common/utils';
import type { Optional } from 'utility-types';
import { useRoute, useRouter } from 'vue-router';
import { useAlertStore } from '@/store/alerts';
import TriangleExclamationIcon from '@vue-patternfly/icons/triangle-exclamation-icon';
import MagnifyingGlassIcon from '@vue-patternfly/icons/magnifying-glass-icon';
import FileLinesIcon from '@vue-patternfly/icons/file-lines-icon';
import FileInvoiceDollarIcon from '@vue-patternfly/icons/file-invoice-dollar-icon';

const defaultTicket: Optional<Ticket, 'id' | 'data'> = {
  id: undefined,
  titolo: '',
  urgente: false,
  data: undefined,
  stato: 'aperto',
  nr_messaggi: 0,
  prodotto: null,
  department: null,
};

const app = useAppStore();
const login = useLoginStore();
const route = useRoute();
const router = useRouter();
const idTicket: Ref<string|number|undefined>= ref(pop(route.params.id));

const { state: productState, result: products } = asyncComputedList(() => new ProductOrdersResource().get());

const { result: ticket, state: ticketState  } = asyncComputed(() => {
  if (idTicket.value !== 'new' && idTicket.value) {
    return new TicketResource().get(idTicket.value);
  }
  return defaultTicket;
}, defaultTicket);

const { result: messages, state: messagesState  } = asyncComputedList(() => {
  if (idTicket.value) {
    return new TicketResource().messages(idTicket.value).get();
  }
  return [];
});
const { result: urgentTicketCost } = asyncComputed(() => new UrgentTicketCostResource().get(1));
const { result: areWeClosed } = asyncComputed(() => new AreWeClosedResource().get(1));

const attachments: Ref<File[]> = ref([]);
const urgente = ref(false);
const department: Ref<'tecnica'|'amministrativa'|'commerciale'|null> = ref(null)

const name = computed(() => ticket.value.id ? _t('Ticket: #{0}', [ticket.value.id]) : _t('Nuovo ticket'));
const title = computed(() => ticket.value.id ? _t('Ticket nr.{0}: {1}', [ticket.value.id, ticket.value.titolo]) : _t('Nuovo ticket') + (ticket.value.urgente ? ` [${_t('Urgente')}]` : ''));

const confirmMessage = computed(() => {
  return urgentTicketCost.value > 0 && urgente.value && !login.is_premium
    ? _t(
      'Il ticket è stato impostato come urgente e verrà quindi fatturato al costo di {0}. Vuoi procedere?',
      [formatCurrency(urgentTicketCost.value, login.currency)],
    )
    : undefined;
});

const additionalData = computed(() => {
  return { attachments: attachments.value };
});


function send(_e: Event | undefined, responseData: unknown) {
  const alertStore = useAlertStore();
  attachments.value = [];
  if (ticket.value.id) {
    alertStore.add({ title: _t('Risposta inviata'), type: 'success', toast: true });
    messagesState.update();
  } else {
    const newTicket = responseData as {
      id: number;
      data: PHPDateTime;
      messaggio: string;
      stato: 'aperto' | 'chiuso';
      titolo: string;
      attachments: any[];
      extra_time: boolean | null;
      urgente: boolean | null;
    };
    idTicket.value = newTicket.id;
    alertStore.add({ title: !newTicket.urgente && newTicket.extra_time ? _t('Ticket inviato. Riceverai risposta in orario di ufficio. Grazie!') : _t('Ticket inviato. Lo staff ti risponderà appena possibile. Grazie!'), type: 'success', toast: true });
    router.push({ name: 'ticket', params: { id: newTicket.id } });
  }
}

function messageHtml(messaggio: string) {
  const appStylesheet = document.querySelector<HTMLLinkElement>('link[rel=stylesheet]')?.outerHTML ?? '';

  return `<html class="${app.darkTheme ? 'pf-v6-theme-dark' : ''}"><head>${appStylesheet}<style>
html, body {
  padding: 0;
  margin: 0;
}

body {
  font-size: 9pt;
  background-color: transparent;
}

body > :first-child {
  margin-top: 0;
}

body > :last-child {
  margin-bottom: 0;
}

a {
  color: #80cfff;
  text-decoration: none;
}

blockquote {
  margin: 4pt 0 4pt 0;
  padding: 0 0 0 1em;
  border-left: 2px solid;
  unicode-bidi: -webkit-plaintext
}

blockquote,
blockquote blockquote blockquote blockquote,
blockquote blockquote blockquote blockquote blockquote blockquote blockquote {
  border-color: #82ff95;
}

blockquote blockquote,
blockquote blockquote blockquote blockquote blockquote,
blockquote blockquote blockquote blockquote blockquote blockquote blockquote blockquote {
  border-color: #69ce78;
}

blockquote blockquote blockquote,
blockquote blockquote blockquote blockquote blockquote blockquote,
blockquote blockquote blockquote blockquote blockquote blockquote blockquote blockquote blockquote {
  border-color: #4b9456;
}

::-webkit-scrollbar {
  transition: width .2s linear;
  width: 8px;
  height: 8px
}

::-webkit-scrollbar-thumb {
  transition: background-color .2s linear;
  background-color: #aaa;
  background-color: #aaaaaa80;
  border-radius: 4px
}

::-webkit-scrollbar-thumb:hover {
  background-color: #999;
  background-color: #999999b3
}

::-webkit-scrollbar-track {
  background-color: transparent
}
</style></head>
<body class="pf-v6-c-content">${messaggio}</body></html>`;
}

function avatar(message: TicketMessage) {
  return `https://www.gravatar.com/avatar/${md5(message.utente ?? '')}?s=45&d=retro`;
}

function refreshAttachments(e: Event) {
  if (!(e.target instanceof HTMLInputElement) || !e.target.files) {
    return;
  }

  for (const file of e.target.files) {
    attachments.value.push(file);
  }
  try {
    e.target.value = '';
  } catch {
    // ignore
  }
}
</script>
